import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import { Fade } from '@mui/material';
import {
  ArrowForward,
  Microsoft,
  VisibilityOutlined as Visibility,
  VisibilityOffOutlined as VisibilityOff,
} from '@mui/icons-material';
import backendUrl from '../store/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { login, clearError, clearFieldErrors, clearSuccess } from '../store/slices/authSlice';
import LightThemeWrapper from '../components/LightThemeWrapper';
import PublicNavbar from '../components/PublicNavbar';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, loading, error, fieldErrors, successMessage } = useAppSelector((state: any) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });

  // Open email form when URL hash requests login
  useEffect(() => {
    if (location.hash === '#login') {
      setShowEmailLogin(true);
    }
  }, [location.hash]);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/getting-started');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleMicrosoftLogin = () => {
    window.location.href = `${backendUrl}/api/auth/microsoft-login`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    dispatch(clearFieldErrors());
    dispatch(login({ email, password }));
  };

  const handleFieldBlur = (field: 'email' | 'password') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };



  return (
    <LightThemeWrapper>
      <PublicNavbar />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pt: '64px' // Account for navbar height
      }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          py: { xs: 4, md: 6 },
          position: 'relative',
          width: '100%'
        }}
      >
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={600}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'center', md: 'center' },
                justifyContent: 'center',
                gap: { xs: 3, md: 6 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Box sx={{ flex: '1 1 0%', maxWidth: { xs: 700, md: 720 } }}>
              {/* Main Title */}
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                  color: '#2d3748',
                }}
              >
                AGRANA AI HUB
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: '#4a5568',
                }}
              >
                Your central platform for AI-driven innovation and intelligence.
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  lineHeight: 1.6,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  maxWidth: '700px',
                  mx: { xs: 'auto', md: 0 },
                  color: '#718096',
                  fontWeight: 300,
                }}
              >
                The AGRANA AI Hub connects all AI initiatives in one place — transforming data into smarter processes, better decisions, and new growth opportunities. It's your single access point to explore use cases, share knowledge, and accelerate AGRANA's transformation through AI-powered innovation.
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 3, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                {isAuthenticated && (
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => navigate('/getting-started')}
                    endIcon={<ArrowForward />}
                    sx={{
                      bgcolor: '#a31830',
                      color: 'white',
                      borderRadius: '6px',
                      px: 3,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 500,
                      boxShadow: '0 2px 8px rgba(200, 30, 59, 0.3)',
                      '&:hover': {
                        bgcolor: '#8b1426',
                        boxShadow: '0 4px 12px rgba(200, 30, 59, 0.4)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Go to Getting Started
                  </Button>
                )}
              </Box>
              </Box>

              {/* Inline Login Card (same page) */}
              {!isAuthenticated && !loading && (
                <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 420 }, maxWidth: 420 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: isMobile ? 2 : 3,
                      mx: 'auto',
                      borderRadius: isMobile ? 2 : 3,
                      width: '100%',
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 3 }}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        component="h1"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: '#000',
                          fontSize: isMobile ? '1.25rem' : undefined,
                        }}
                      >
                        Welcome Back
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                      >
                        Sign in to your AGRANA account
                      </Typography>
                    </Box>

                    {error && (
                      <Box sx={{ mb: 2 }}>
                        {/* reuse existing UI pattern from login page by rendering error as simple text */}
                        <Typography color="error" variant="body2">
                          {error}
                        </Typography>
                      </Box>
                    )}
                    {successMessage && (
                      <Box sx={{ mb: 2 }}>
                        <Typography color="success.main" variant="body2">
                          {successMessage}
                        </Typography>
                      </Box>
                    )}

                    <Button
                      fullWidth
                      variant="outlined"
                      size={isMobile ? 'small' : 'medium'}
                      startIcon={<Microsoft />}
                      onClick={handleMicrosoftLogin}
                      disabled={loading}
                      sx={{
                        mb: isMobile ? 2 : 2.5,
                        py: isMobile ? 1 : 1.2,
                        borderColor: '#a31830',
                        color: '#a31830',
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        '&:hover': {
                          borderColor: '#8b1426',
                          backgroundColor: 'rgba(163, 24, 48, 0.04)',
                        },
                      }}
                    >
                      Continue with Microsoft
                    </Button>

                    <Box sx={{ textAlign: 'center', my: isMobile ? 2 : 2.5 }}>
                      <Button
                        onClick={() => setShowEmailLogin(!showEmailLogin)}
                        size="small"
                        sx={{
                          color: 'text.secondary',
                          textTransform: 'none',
                          fontSize: isMobile ? '0.75rem' : '0.8rem',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        {showEmailLogin ? 'Hide email login' : 'Or sign in with email'}
                      </Button>
                    </Box>

                    <Collapse in={showEmailLogin}>
                      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="email"
                          label="Email Address"
                          name="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onBlur={() => handleFieldBlur('email')}
                          size="small"
                          error={!!(fieldErrors?.email && touched.email)}
                          helperText={(fieldErrors?.email && touched.email) ? fieldErrors.email[0] : ''}
                          sx={{ mb: isMobile ? 1 : 1.5 }}
                        />
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          name="password"
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onBlur={() => handleFieldBlur('password')}
                          size="small"
                          error={!!(fieldErrors?.password && touched.password)}
                          helperText={(fieldErrors?.password && touched.password) ? fieldErrors.password[0] : ''}
                          sx={{ mb: isMobile ? 2 : 2.5 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size={isMobile ? 'small' : 'medium'}
                          disabled={loading}
                          sx={{
                            mt: isMobile ? 1.5 : 2,
                            mb: isMobile ? 1.5 : 2,
                            py: isMobile ? 1 : 1.2,
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                            backgroundColor: '#a31830',
                            '&:hover': {
                              backgroundColor: '#8b1426',
                            },
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            'Sign In'
                          )}
                        </Button>
                      </Box>
                    </Collapse>
                  </Paper>
                </Box>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>


      </Box>
    </LightThemeWrapper>
  );
};

export default Home;